import { KpiItem } from '@app/shared/components/overview';
import { ExportTable, PdfBreakdownTable } from '@core/models/reporting.model';

export interface ProjectCreate {
    project_name: string;
    campaign_ids: string[];
    user_id: number;
}

// Sent to POST /project/reporting-pdf. A project bundles campaigns of different lever types,
// so `tables` holds one cross-type summary table plus one detail table per lever type — all
// user-selectable, same shape as the campaign report's breakdown tables (reporting.model.ts).
export interface ProjectPdfExportPayload {
    projectId: number;
    projectName: string;
    user_id: number | string;
    client_ids: string[];
    generatedAt: string;
    dateBegin: string;
    dateEnd: string;
    currency: string;
    kpis: KpiItem[];
    graphsData: {
        date_range: string[];
        chart_impression: number[];
        chart_clicks: number[];
        chart_ctr: number[];
    };
    tables: PdfBreakdownTable[];
}

export type ProjectExcelExportTable = ExportTable;

export interface CampaignData {
    insertion_order_id: string;
    advertiser_name: string;
    insertion_order_name: string;
    begin_date: string;
    begin_date_formatted: string;
    end_date_formatted: string;
    end_date: string;
    budget_offer: number;
    type: string;
    access_campaign_id: number;
    access_campaign_active: number;
    impressions: number;
    spent: number;
    cpm: number;
    clicks: number;
    ctr: number;
    conversions: number;
    convs_rate: number;
    cpc: number;
    ca: number;
    conversions_value: number;
    data_by_day: DayData[];
    device_data: DeviceData[];
    is_stopped: boolean;
    state: boolean;
    is_end_date_updated: boolean;
    is_budget_updated: boolean;
    // Audio specific fields
    '25_pcts'?: number;
    '50_pcts'?: number;
    '75_pcts'?: number;
    completions?: number;
    completion_rate?: number;
    skips?: number;
    skip_rate?: number;
    starts?: number;
    start_rate?: number;
}

export interface DayData {
    date: string;
    date_formatted: string;
    impressions: string;
    spent: number;
    cpm: number;
    clicks: string;
    ctr: string;
    conversions: string;
    convs_rate: string;
    cpc: number | null;
    ca: number;
    conversions_value: number;
    roas: number;
    // Audio specific fields
    '25_pcts'?: string;
    '50_pcts'?: string;
    '75_pcts'?: string;
    completions?: string;
    completion_rate?: number;
    skips?: string;
    skip_rate?: number;
    starts?: string;
    start_rate?: number;
}

export interface DeviceData {
    device_type?: string;
    device?: { fr: string; en: string; es: string; origin: string };
    clicks: number | string;
    convs_rate?: number | string;
    ctr: number | string;
    impressions: number | string;
    spent: number | string;
    total_conv?: number | string;
}

export interface MetricsByType {
    type: string;
    impressions: number;
    spent: number;
    cpm: number;
    clicks: number;
    ctr: number;
    conversions: number;
    convs_rate: number;
    cpc: number;
    ca: number;
    conversions_value: number;
    roas: number;
    // Audio specific fields
    '25_pcts'?: number;
    '50_pcts'?: number;
    '75_pcts'?: number;
    completions?: number;
    completion_rate?: number;
    skips?: number;
    skip_rate?: number;
    starts?: number;
    start_rate?: number;
}

export interface ProjectCampaignResponse {
    datas: CampaignData[];
    datas_only_metrics_by_type: MetricsByType[];
    campaign_ids: string[];
}

export class Project {
    id: number;
    name: string;
    campaign_ids: string[];
    user_id: number;
    campaignData?: ProjectCampaignResponse;

    constructor(data: { id: number; name: string; campaign_ids: string[]; user_id: number }) {
        this.id = data.id;
        this.name = data.name;
        this.campaign_ids = data.campaign_ids;
        this.user_id = data.user_id;
    }

    // Set campaign data when loaded
    setCampaignData(data: ProjectCampaignResponse): void {
        this.campaignData = data;
    }

    // Calculate total impressions across all campaigns
    getTotalImpressions(): number {
        if (!this.campaignData?.datas) return 0;
        return this.campaignData.datas.reduce((total, campaign) => total + (campaign.impressions ? Number(campaign.impressions) : 0), 0);
    }

    // Calculate total spent across all campaigns
    getTotalSpent(): number {
        if (!this.campaignData?.datas) return 0;
        const total = this.campaignData.datas.reduce((sum, campaign) => sum + (campaign.spent ? Number(campaign.spent) : 0), 0);
        return Math.round(total * 100) / 100;
    }

    // Calculate total clicks across all campaigns
    getTotalClicks(): number {
        if (!this.campaignData?.datas) return 0;
        return this.campaignData.datas.reduce((total, campaign) => total + (campaign.clicks ? Number(campaign.clicks) : 0), 0);
    }

    getTotalConversions(): number {
        if (!this.campaignData?.datas) return 0;
        return this.campaignData.datas.reduce((total, campaign) => total + (campaign.conversions ? Number(campaign.conversions) : 0), 0);
    }

    getTotalBudget(): number {
        if (!this.campaignData?.datas) return 0;
        return this.campaignData.datas.reduce((total, campaign) => total + (campaign.budget_offer ? Number(campaign.budget_offer) : 0), 0);
    }

    getTotalRoas(): number {
        if (!this.campaignData?.datas) return 0;
        return this.getTotalConversions() / this.getTotalSpent();
    }

    // Calculate overall CTR
    getOverallCTR(): number {
        const totalImpressions = this.getTotalImpressions();
        const totalClicks = this.getTotalClicks();
        return totalImpressions > 0 ? Math.round((totalClicks / totalImpressions) * 100 * 100) / 100 : 0;
    }

    // Calculate overall CPC
    getOverallCPC(): number {
        const totalClicks = this.getTotalClicks();
        const totalSpent = this.getTotalSpent();
        return totalClicks > 0 ? Math.round((totalSpent / totalClicks) * 100) / 100 : 0;
    }

    // Calculate overall CPM
    getOverallCPM(): number {
        const totalImpressions = this.getTotalImpressions();
        const totalSpent = this.getTotalSpent();
        return totalImpressions > 0 ? Math.round((totalSpent / totalImpressions) * 1000 * 100) / 100 : 0;
    }

    // Get campaigns by type
    getCampaignsByType(type: string): CampaignData[] {
        if (!this.campaignData?.datas) return [];
        return this.campaignData.datas.filter((campaign) => campaign.type === type);
    }

    // Get all campaign types
    getCampaignTypes(): string[] {
        if (!this.campaignData?.datas) return [];
        return [...new Set(this.campaignData.datas.map((campaign) => campaign.type))];
    }

    // Get metrics by type (from API aggregated data)
    getMetricsByType(): MetricsByType[] {
        return this.campaignData?.datas_only_metrics_by_type || [];
    }

    // Get date range of project
    getDateRange(): { start: Date | null; end: Date | null } {
        if (!this.campaignData?.datas) return { start: null, end: null };

        const dates = this.campaignData.datas.flatMap((campaign) => [new Date(campaign.begin_date), new Date(campaign.end_date)]);

        return {
            start: dates.length > 0 ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null,
            end: dates.length > 0 ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null,
        };
    }

    // Get performance summary
    getPerformanceSummary() {
        return {
            totalImpressions: this.getTotalImpressions(),
            totalSpent: this.getTotalSpent(),
            totalClicks: this.getTotalClicks(),
            totalConversions: this.getTotalConversions(),
            totalRoas: this.getTotalRoas(),
            totalBudget: this.getTotalBudget(),
            overallCTR: this.getOverallCTR(),
            overallCPC: this.getOverallCPC(),
            overallCPM: this.getOverallCPM(),
            campaignCount: this.campaignData?.datas?.length || 0,
            campaignTypes: this.getCampaignTypes(),
            dateRange: this.getDateRange(),
        };
    }

    // Check if campaign data is loaded
    hasCampaignData(): boolean {
        return !!this.campaignData;
    }

    // Get campaign by ID
    getCampaignById(id: string): CampaignData | undefined {
        if (!this.campaignData?.datas) return undefined;
        return this.campaignData.datas.find((campaign) => campaign.insertion_order_id === id);
    }

    isOnlyDooh(): boolean {
        if (!this.campaignData?.datas) return false;
        return this.campaignData.datas.every((campaign) => campaign.type === 'dooh');
    }
}
